<template>
  <section class="wrapper-list-attachments">
    <b-loading data-cy="LOADING" :active="isLoading" />
    <div v-if="listFiles.length">
      <div slot="header" role="group" class="mt-15 mb-5 is-flex">
        <div class="list-header columns m-0 is-mobile is-vcentered">
          <div class="column is-flex is-narrow p-0 ml-5 width-30">
            <img
              v-if="!isMobile && listFiles.length"
              :src="isContrast ? arrowsContrast : arrows"
              width="15"
              height="15"
              alt=""
            />
            <b-checkbox
              v-if="listFiles.length && !(isMobile && isJoint)"
              v-model="checkAll"
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              class="pl-10"
              role="checkbox"
            />
          </div>
          <div class="column p-0">
            <div class="columns ml-10 is-mobile">
              <label
                class="is-hidden-mobile has-text-color-7 column is-size-7"
                :class="isArchived ? `is-6` : `is-two-fifths`"
              >
                {{ General.ATTACHED_FILE }}
              </label>
              <div class="column">
                <b-dropdown
                  :triggers="['hover']"
                  aria-role="list"
                  position="is-bottom-right"
                  class="is-hidden-mobile"
                >
                  <template slot="trigger">
                    <label
                      class="
                        has-text-color-7 is-size-7 is-flex is-align-items-flex-start
                      "
                    >
                      {{ General.DOCUMENT_TYPE }}
                      <b-icon icon="chevron-down" />
                    </label>
                  </template>
                  <b-dropdown-item custom>
                    <span class="is-size-9">{{
                      General.TEXTS.TYPE_SELECTION_BATCH
                    }}</span>
                  </b-dropdown-item>
                  <b-dropdown-item custom>
                    <b-autocomplete
                      v-model="documentTypeSelected"
                      :data="filterTypes"
                      autocomplete
                      field="type"
                      name="id"
                      :disabled="!showChangeType"
                      :placeholder="General.SELECT_DOCUMENT_TYPE"
                      keepFirst
                      role="combobox"
                      @typing="getFilterTypes"
                      @select="option => selectType(option)"
                      @input="value => !value && selectType()"
                    >
                      <template slot-scope="props">
                        <div
                          class="is-flex is-justify-content-space-between custom-bottom-border"
                        >
                          <div class="pre-wrap">
                            {{ props.option.type }}
                          </div>
                          <b-tag
                            v-if="props.option.to_publish"
                            rounded
                            :type="isContrast ? 'is-white' : 'is-blue-oil'"
                          >
                            <span>{{ General.DOE }}</span>
                          </b-tag>
                        </div>
                      </template>
                    </b-autocomplete>
                  </b-dropdown-item>
                  <b-dropdown-item custom>
                    <div class="is-flex is-justify-content-center">
                      <b-button
                        class="has-text-weight-semibold"
                        :type="isContrast ? 'is-white-dark' : 'is-primary'"
                        :disabled="!showChangeType"
                        role="button"
                        @click.stop="applyName"
                      >
                        {{ General.CHANGE }}
                      </b-button>
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
      <draggable
        v-if="listFiles.length"
        v-model="listFiles"
        class="list-group"
        ghostClass="ghost"
        :disabled="editables || isArchived || isRevertNoEffect"
        handle=".handler"
      >
        <div
          v-for="file in listFiles"
          :key="file.id"
          class="container-attached-doc"
        >
          <div
            v-if="!file.primary && !(isMobile && isJoint)"
            class="column p-0 ml-5 is-narrow width-30 is-flex custom-align"
          >
            <span class="handler">
              <b-icon
                v-if="!isMobile"
                icon="drag-horizontal-variant"
                type="is-light"
              />
            </span>
            <b-checkbox
              v-model="selectedDocument"
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              class="pl-10"
              :nativeValue="file"
            />
          </div>
          <DisplayAttachedDoc
            :file="file"
            :isEditable="file.editable || isDispatchEditable"
            :onEdit="onEdit"
            :isArchived="isArchived"
            :isJoint="isJoint"
            :showSignAlertModal="showSignAlertModal"
            :process="getProcessObject"
            :showPDF="showPDF"
            :isSignatureDone="isSignatureDone"
            :tooltipOpenMobile="tooltipOpenMobile"
            :disableDoeSizeLimit="maxAttachmentSizeReached"
            :removeFile="removeFile"
            :isRevertNoEffect="isRevertNoEffect"
          />
        </div>
      </draggable>
    </div>
    <slot name="detail" />
    <div class="buttons custom-direction" :class="isJoint ? 'mt-5' : 'mt-20'">
      <b-pagination
        v-if="value && countFiles"
        :total="countFiles"
        :perPage="perPage"
        :current.sync="currentPage"
        rangeBefore="1"
        rangeAfter="1"
        order="is-centered"
        class="is-full"
        rounded
        iconPrev="chevron-left"
        iconNext="chevron-right"
        :aria-next-label="General.NEXT_PAGE"
        :aria-previous-label="General.PREVIOUS_PAGE"
        :aria-page-label="General.PAGE"
        :aria-current-label="General.CURRENT_PAGE"
        role="navigation"
      />
      <div class="is-full">
        <b-button
          v-if="isDispatchEditable"
          :type="isContrast ? 'is-white-dark' : 'is-primary'"
          iconLeft="plus-circle-outline"
          outlined
          @click="editableDocument(false, 7, true)"
          >{{ General.CREATE_DOCUMENT }}</b-button
        >
        <b-button
          v-else-if="canSign && editables"
          :type="isContrast ? 'is-white-dark' : 'is-primary'"
          iconLeft="plus-circle-outline"
          outlined
          @click="editableDocument(false, -1, true)"
          >{{ General.CREATE_DOCUMENT }}</b-button
        >
        <b-tooltip
          v-else-if="canSign && !isArchived && !isJoint && !isRevertNoEffect"
          :active="maxAttachmentSizeReached"
          :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
          position="is-top"
          class="custom-mr"
          multilined
        >
          <template v-slot:content>
            <span class="has-text-weight-semibold is-flex">{{
              `${General.ALERT}!`
            }}</span>
            <p class="has-text-left">
              {{ General.TEXTS.UNABLE_CREATE_DOCUMENT_SIZE }}
            </p>
          </template>
          <b-button
            :disabled="maxAttachmentSizeReached"
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            class="mr-5"
            iconLeft="plus-circle-outline"
            outlined
            @click="documentEditor(7, false)"
            >{{ General.CREATE_DOCUMENT }}</b-button
          >
        </b-tooltip>
        <b-tooltip
          :active="maxAttachmentSizeReached"
          :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
          position="is-top"
          multilined
        >
          <template v-slot:content>
            <span class="has-text-weight-semibold is-flex">{{
              `${General.ALERT}!`
            }}</span>
            <p class="has-text-left">
              {{ General.TEXTS.UNABLE_TO_ATTACH_DOCUMENT }}
            </p>
          </template>
          <b-button
            :disabled="maxAttachmentSizeReached"
            data-cy="ATTACH_DOCUMENT"
            class="mr-5"
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            iconLeft="upload"
            outlined
            @click="showModalUpload(editables || isDispatchEditable)"
            >{{ General.ATTACH_DOCUMENT }}</b-button
          >
        </b-tooltip>
      </div>
      <template
        v-if="!editables && !isDispatchEditable && selectedDocument.length > 1"
      >
        <b-tooltip
          multilined
          :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
          size="is-large"
          :position="isArchived && isRevertNoEffect ? 'is-right' : 'is-top'"
          :active="hasSignCurrentUserDoc"
        >
          <template v-slot:content>
            <span class="has-text-weight-semibold is-flex">{{
              App.MenuMain.HELP
            }}</span>
            <p class="has-text-left">
              {{ General.TEXTS.UNABLE_TO_GIVE_KNOWLEDGE }}
            </p>
          </template>
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            @click.stop="validateBatchFile"
          >
            {{ General.GIVE_KNOWLEDGE }}
          </b-button>
        </b-tooltip>
        <b-tooltip
          multilined
          :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
          size="is-large"
          position="is-top"
          :active="hasSignCurrentUserDoc || hasLargeFile"
        >
          <template v-slot:content>
            <span class="has-text-weight-semibold is-flex">{{
              App.MenuMain.HELP
            }}</span>
            <p class="has-text-left">
              {{ General.TEXTS.UNABLE_TO_SIGN }}
            </p>
          </template>
          <b-button
            class="mx-5"
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            :disabled="hasSignCurrentUserDoc || hasLargeFile"
            @click.stop="signBatchDocument"
          >
            {{ General.SIGN }}
          </b-button>
        </b-tooltip>
        <b-tooltip
          v-if="!isArchived && !isRevertNoEffect"
          multilined
          :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
          size="is-large"
          position="is-top"
          :active="hasLargeFile"
        >
          <template v-slot:content>
            <span class="has-text-weight-semibold is-flex">{{
              App.MenuMain.HELP
            }}</span>
            <p class="has-text-left">
              {{ General.TEXTS.UNABLE_TO_SIGN }}
            </p>
          </template>
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            :disabled="hasLargeFile"
            @click.stop="requestSubscriptionBatchFile"
          >
            {{ General.REQUEST_SUBSCRIPTION }}
          </b-button>
        </b-tooltip>
        <b-button
          v-if="!isArchived && !isRevertNoEffect"
          class="ml-5"
          :type="isContrast ? 'is-white-dark' : 'is-primary'"
          @click.stop="downloadBatchPDF"
        >
          {{ General.EXPORT_PDF }}
        </b-button>
      </template>
      <b-button
        v-if="
          isDEV &&
            selectedDocument.length > 1 &&
            !isArchived &&
            !isRevertNoEffect
        "
        class="ml-5"
        :type="isContrast ? 'is-white-dark' : 'is-primary'"
        @click.stop="deleteBatchPDF"
      >
        {{ General.DELETE }}
      </b-button>
    </div>
    <ProgressBar
      v-if="!editables && !isDispatchEditable"
      :barLabel="General.TEXTS.LIMIT_PROCESS_ATTACHMENT"
      :currentSize="getCurrentSize"
      :maxSize="PROCESS_FILES_MAX_SIZE"
    />
  </section>
</template>

<script>
import draggable from "vuedraggable";
import { mapActions, mapGetters, mapMutations } from "vuex";

import { App, General } from "@/utils/constants";
import {
  DOC_SIGNATURES_STATUS as DOC_SIGN,
  LIST_TYPE as listType,
  PROCESS_FILES_MAX_SIZE,
  MAX_SIGN_SIZE,
  ATTACHEMENT_MAX_COUNT
} from "@/utils/enumerate";
import arrows from "@/assets/svg/arrows.svg";
import arrowsContrast from "@/assets/svg/arrows_contrast.svg";
import alignJustify from "@/assets/svg/align-justify-solid.svg";
import { exportFilesPDF } from "@/utils/fileUtility";
import ModalSignDocument from "@/views/App/ModalSignDocument";
import ModalDocSignature from "@/views/App/ModalDocSignature";
import ModalSelectUser from "@/views/App/ModalSelectUser";
import ModalWarning from "@/views/App/ModalWarning";
import DisplayAttachedDoc from "@/components/DisplayAttachedDoc";
import ProgressBar from "@/components/ProgressBar";
import { debounce } from "lodash";

export default {
  name: "ListAttachments",
  components: {
    draggable,
    DisplayAttachedDoc,
    ProgressBar
  },
  props: {
    onEdit: {
      type: Function,
      default: () => null
    },
    documentEditor: {
      type: Function,
      default: () => null
    },
    editableDocument: {
      type: Function,
      default: () => null
    },
    showModalUpload: {
      type: Function,
      default: () => null
    },
    processNup: {
      type: String,
      default: ""
    },
    editables: {
      type: Boolean,
      default: false
    },
    isJoint: {
      type: Boolean,
      default: false
    },
    isDispatchEditable: {
      type: Boolean,
      default: false
    },
    isDispatch: {
      type: Boolean,
      default: false
    },
    canSign: {
      type: Boolean,
      default: true
    },
    isArchived: {
      type: Boolean,
      default: false
    },
    showSignAlertModal: {
      type: Boolean,
      default: false
    },
    isSignatureDone: {
      type: Boolean,
      default: false
    },
    process: {
      type: Object,
      default: () => {}
    },
    showPDF: {
      type: Boolean,
      default: false
    },
    value: {
      type: Number,
      default: null
    },
    tooltipOpenMobile: {
      type: String,
      default: "is-right"
    },
    isRevertNoEffect: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      App,
      General,
      arrows,
      arrowsContrast,
      alignJustify,
      DOC_SIGN,
      listType,
      PROCESS_FILES_MAX_SIZE,
      checkAll: false,
      selectedDocument: [],
      documentTypeSelected: "",
      filterTypes: [],
      type_id: 0,
      isLoading: false,
      perPage: ATTACHEMENT_MAX_COUNT
    };
  },

  computed: {
    ...mapGetters({
      getModifiedProcessFiles: "getModifiedProcessFiles",
      getProcessFilesEditable: "getModifiedProcessFilesEditable",
      getProcessFilesEditableCount: "getProcessFilesEditableCount",
      getProcessFilesCount: "getProcessFilesCount",
      getDispatchFilesEditablesCount: "getDispatchFilesEditablesCount",
      getDispatchFilesCount: "getDispatchFilesCount",
      getModifiedJointFiles: "getModifiedJointFiles",
      getJointFilesCount: "getJointFilesCount",
      getListAttachmentCount: "getListAttachmentCount",
      getCurrentJoint: "getCurrentJoint",
      getModifiedDispatchFilesEditable: "getModifiedDispatchFilesEditable",
      getModifiedDispatchFiles: "getModifiedDispatchFiles",
      getListAttachment: "getListAttachment",
      getFileTypeById: "getFileTypeById",
      getListSignatureDocuments: "getListSignatureDocuments",
      getNewProcess: "getNewProcess",
      getCurrentProcess: "getCurrentProcess",
      getCurrentUser: "getCurrentUser",
      fileTypes: "getFileTypesByEditor",
      getDraftFilesSize: "getDraftFilesSize",
      getProcessFileSize: "getProcessFileSize",
      initialDispatchFile: "getDispatchInitialFile",
      getDispatch: "getDispatch",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    isDEV() {
      return process.env.VUE_APP_ENVIRONMENT === "development";
    },

    messageDeleteAttachment() {
      return this.General.MESSAGES.DO_YOU_WANT_DELETE_REQUEST_PLURAL;
    },

    countFiles() {
      if (this.isArchived || this.isRevertNoEffect)
        return this.getListAttachmentCount;
      if (this.isDispatch) {
        const hasPrimaryFile = Boolean(
          this.initialDispatchFile(this.getDispatch?.id)
        );
        return this.getDispatchFilesCount - (hasPrimaryFile ? 1 : 0);
      }
      if (this.isDispatchEditable) return this.getDispatchFilesEditablesCount;
      if (this.isJoint) return this.getJointFilesCount;
      if (this.editables) return this.getProcessFilesEditableCount;
      const hasPrimaryFile = Boolean(
        this.initialDispatchFile(this.getProcessId)
      );
      return this.getProcessFilesCount - (hasPrimaryFile ? 1 : 0);
    },

    listFiles: {
      get() {
        if (this.isArchived || this.isRevertNoEffect)
          return this.getListAttachment;
        if (this.editables) return this.getProcessFilesEditable;
        if (this.isJoint) return this.getModifiedJointFiles;
        if (this.isDispatch) return this.getModifiedDispatchFiles;
        if (this.isDispatchEditable)
          return this.getModifiedDispatchFilesEditable;

        return this.getModifiedProcessFiles;
      },
      async set(value) {
        const sequences = value
          .map(file => file.sequence_draft)
          .sort((first, second) => first - second);
        const newList = value.map((file, index) => ({
          ...file,
          sequence_draft: sequences[index]
        }));
        await this.$store.dispatch("orderProcessFiles", newList);

        if (this.isDispatch)
          this.$store.dispatch("dispatchFileRequest", {
            dispatchId: this.$store.getters.getDispatch.id
          });
      }
    },

    getProcessId() {
      return this.isDispatch ||
        this.isDispatchEditable ||
        this.isJoint ||
        this.isArchived ||
        this.isRevertNoEffect
        ? this.getCurrentProcess.id
        : this.getNewProcess.id;
    },

    getProcessObject() {
      return this.isDispatch ||
        this.isJoint ||
        this.isArchived ||
        this.isRevertNoEffect
        ? this.getCurrentProcess
        : this.getNewProcess;
    },

    hasLargeFile() {
      return this.selectedDocument.some(doc => doc.filesize > MAX_SIGN_SIZE);
    },

    hasSignCurrentUserDoc() {
      return this.selectedDocument.some(doc =>
        doc.sign_history.some(
          history =>
            !history.only_historical &&
            history.status === this.DOC_SIGN.SIGNED &&
            history.user_id === this.getCurrentUser.id
        )
      );
    },

    showChangeType() {
      return (
        this.selectedDocument.length > 1 &&
        !this.selectedDocument.some(
          file =>
            file.is_doe_document_model ||
            file.to_publish ||
            file.is_signed ||
            file.status === "PENDING"
        )
      );
    },

    getCurrentSize() {
      if (this.isArchived || this.isRevertNoEffect)
        return (
          this.getDraftFilesSize("processAttachmentFiles") +
          this.getProcessFileSize
        );
      else if (this.isDispatch || this.isDispatchEditable)
        return (
          this.getDraftFilesSize("dispatchFiles") + this.getProcessFileSize
        );
      else if (this.isJoint)
        return this.getDraftFilesSize("jointFiles") + this.getProcessFileSize;
      else return this.getProcessFileSize;
    },

    maxAttachmentSizeReached() {
      return this.getCurrentSize >= PROCESS_FILES_MAX_SIZE;
    },

    currentPage: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      }
    }
  },

  watch: {
    activeTab() {
      this.checkAll = false;
      this.selectedDocument = [];
    },
    checkAll(value) {
      if (value) this.selectedDocument = this.listFiles;
      else this.selectedDocument = [];
    },
    selectedDocument(value) {
      const checkAllItens = value.length == this.listFiles.length;
      let status = null;
      if (checkAllItens) status = checkAllItens;
      else status = value.length === 0 ? false : -1;
      if (status !== -1 && this.checkAll !== status) this.checkAll = status;
    },
    listFiles(newValue) {
      this.addFile(newValue);
    }
  },

  beforeDestroy() {
    const clearFile = {
      results: [],
      count: 0
    };

    if (this.isArchived || this.isRevertNoEffect)
      this.setProcessFileAttachment(clearFile);
    else if (this.isJoint) this.setJointFiles(clearFile);
    else if (this.isDispatch) this.dispatchFileSuccess(clearFile);
    else if (this.isDispatchEditable) this.dispatchFileEditable(clearFile);
    else if (this.editables) this.setProcessFileEditable(clearFile);
    else this.setProcessFile(clearFile);

    const actualPath = this.$router.currentRoute.path;
    const processDetailPath = `/app/processos/detalhamento/${this.getProcessId}`;
    if (
      (!this.isArchived && !this.isRevertNoEffect) ||
      actualPath != processDetailPath
    )
      this.setProcessFileSize(0);
  },

  methods: {
    ...mapActions({
      editDocumentTypeBatch: "editDocumentTypeBatch",
      deleteProcessFileRequest: "deleteProcessFileRequest",
      requestFile: "requestDataProcessFile",
      getJointFile: "getJointFile",
      requestProcessFiles: "processFilesFilteredRequest"
    }),
    ...mapMutations({
      setListSelectedDocuments: "setListSelectedDocuments",
      setCurrentProcessFileList: "setCurrentProcessFileList",
      setProcessFile: "setProcessFile",
      setProcessFileEditable: "setProcessFileEditable",
      dispatchFileSuccess: "dispatchFileSuccess",
      dispatchFileEditable: "dispatchFileEditable",
      setJointFiles: "setJointFiles",
      setProcessFileAttachment: "setProcessFileAttachment",
      setProcessFileSize: "setProcessFileSize"
    }),
    getDocumentType(documentTypeId) {
      if (documentTypeId)
        return this.$store.getters.getFileTypeById(documentTypeId).type;
      return null;
    },
    async refreshProcessFile() {
      this.isLoading = true;
      const files_id = this.selectedDocument.map(file => file.id);
      if (files_id.length) {
        if (this.isJoint && this.selectedDocument[0].document_join_id) {
          await this.getJointFile({
            jointId: this.selectedDocument[0].document_join_id,
            page: 1,
            paginator_count: ATTACHEMENT_MAX_COUNT,
            files_id: JSON.stringify(files_id)
          });
        } else {
          await this.requestProcessFiles({
            files_id,
            processId: this.getProcessId
          });
        }
        this.selectedDocument = [];
      }
      this.isLoading = false;
    },
    handleDocumentSelectedSign() {
      this.setListSelectedDocuments({
        documents: this.selectedDocument.map(doc => ({
          process_file: {
            ...doc,
            document_type: {
              description: this.getFileTypeById(doc.document_type).type
            },
            process: { nup: this.processNup || General.TEXTS.NUP_NOT_GENERATED }
          }
        })),
        capacity_id: this.getCurrentUser.capacity.capacity_id
      });
    },
    signBatchDocument() {
      this.handleDocumentSelectedSign();
      this.selectedDocument = [];

      this.$buefy.modal.open({
        parent: this,
        component: ModalSignDocument,
        props: {
          currentType: this.listType.BATCH_SIGN_FILES,
          isArchived: this.isArchived
        },
        hasModalCard: true,
        trapFocus: true
      });
    },
    async validateBatchFile() {
      this.isLoading = true;
      this.$buefy.modal.open({
        parent: this,
        component: ModalSelectUser,
        props: {
          fileIds: this.selectedDocument,
          process: { id: this.getProcessId },
          updateFiles: this.refreshProcessFile
        },
        hasModalCard: true,
        trapFocus: true
      });
      this.isLoading = false;
    },
    requestSubscriptionBatchFile() {
      this.setCurrentProcessFileList(this.selectedDocument);

      this.$buefy.modal.open({
        parent: this,
        component: ModalDocSignature,
        props: {
          processId: this.getProcessId,
          updateFiles: this.refreshProcessFile
        },
        hasModalCard: true,
        trapFocus: true
      });
    },
    async downloadBatchPDF() {
      this.isLoading = true;
      const result = await this.requestFile({
        fileIds: this.selectedDocument.map(doc => ({
          process_file_id: doc.id
        })),
        processId: this.getProcessId
      });
      if (result) {
        await exportFilesPDF(
          result,
          this.processNup ||
            this.selectedDocument[0].nup ||
            General.TEXTS.NUP_NOT_GENERATED
        );
        this.selectedDocument = [];
      }
      this.isLoading = false;
    },
    async deleteBatchPDF() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: this.messageDeleteAttachment,
          confirm: async () => {
            for (const document of this.selectedDocument)
              await this.deleteProcessFileRequest({
                file: document,
                isArchived: this.isArchived,
                isRevertNoEffect: this.isRevertNoEffect
              });
            this.removeFile();
            this.refreshProcessFile();
          },
          buttons: [
            { label: General.YES, isConfirm: false, action: true },
            { label: General.NO, isConfirm: true, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },
    getFilterTypes(text) {
      this.filterTypes = this.fileTypes(1).filter(option =>
        option.type.toLowerCase().includes(text.toLowerCase())
      );
    },
    selectType(option) {
      if (option) {
        this.type_id = option.id;
        this.documentTypeSelected = option.type;
      }
    },
    async applyName() {
      const process_file_ids = this.selectedDocument.map(
        document => document.id
      );
      await this.editDocumentTypeBatch({
        process_id: this.getProcessId,
        document_type_id: this.type_id,
        process_file_ids
      });
      this.documentTypeSelected = "";
      this.refreshProcessFile();
    },
    removeFile() {
      if (this.listFiles.length === 0 && this.currentPage > 1) {
        this.currentPage = this.currentPage - 1;
      }
    },
    addFile: debounce(function(newValue) {
      if (newValue.length > ATTACHEMENT_MAX_COUNT)
        this.currentPage = this.currentPage + 1;
    }, 2e3)
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.custom-list {
  display: flex;
  justify-content: flex-start;
}

.custom-list li {
  font-weight: 500;
  padding: 5px 0;
}

.custom-list li:nth-child(1) {
  display: flex;
  flex: 0.4;
}

.custom-list li:nth-child(2) {
  display: flex;
  flex: 0.3;
}

.list-header {
  font-weight: bold;
  width: 100%;
}

.ghost {
  opacity: 0.5;
  background-color: #dcdcdc;
}
.container-attached-doc {
  display: flex;
}

.pre-wrap {
  white-space: pre-wrap;
  border: none;
}

.handler {
  cursor: move;
  display: flex;
  align-items: center;
}

.container-attached-doc:hover .handler {
  opacity: 1;
}

.dropdown-item:hover {
  background-color: $color-1 !important;
}

.autocomplete .dropdown-item:hover {
  background-color: $color-2 !important;
}

@media screen and (max-width: 833px) {
  .custom-align {
    align-items: flex-start;
    margin-top: 10px;
  }

  .custom-direction {
    flex-direction: column-reverse;

    .b-tooltip,
    button {
      width: 100%;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/scss/variables.scss";
.wrapper-list-attachments {
  .dropdown-content {
    @media screen and (min-width: 1024px) {
      min-width: 220px;
    }
  }
}
</style>
